import React, { useState, useEffect } from 'react';
import { Button, Dropdown } from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import FileLoaderService, { extractTypes, extractStatuses } from '../services/file-loader-service';
import download from '../utils/download';
import { fileStatusChange, fileTriggered } from '../utils/events';
import { parseCsv, createCsv } from '../utils/file-utils';

const FileExtractButton = ( props ) => {
    const {
        fileId,
        disabled,
        onFileExport,
        additionalColumns,
        onDownload,
        getColumnOrder,
        auth
    } = props;
    const [ file, setFile ] = useState( null );
    const [ exporting, setExporting ] = useState( false );
    const [ menuVisible, setMenuVisible ] = useState( false );

    useEffect( () => {
        if ( !fileId ) {
            return;
        }
        const loadFile = async () => {
            try {
                const fileLoader = FileLoaderService( auth.state );
                const nextFile = await fileLoader.getFile( fileId );
                setFile( nextFile );
            } catch ( error ) {
                console.error( 'Error fetching file', error.status );
                setFile( null );
            }
        };
        loadFile();

        const handleFileStatusChange = ( id ) => {
            if ( id === fileId ) {
                loadFile();
            }
        };

        const handleFileTriggered = ( id ) => {
            setTimeout( () => {
                handleFileStatusChange( id );
            }, 5000 );
        };

        fileStatusChange.on( handleFileStatusChange );
        fileTriggered.on( handleFileTriggered );

        return () => {
            fileStatusChange.off( handleFileStatusChange );
            fileTriggered.off( handleFileTriggered );
        };
    }, [ fileId, auth?.state?.claims?.company_id ] );

    const exportFile = async ( extractType ) => {
        setExporting( true );
        try {
            const fileLoader = FileLoaderService( auth.state );
            const { extractId } = await fileLoader.beginExtract( fileId, extractType, additionalColumns );

            let result;
            do {
                result = await fileLoader.getExtractStatus( fileId, extractType, extractId );

                if ( result.status === extractStatuses.PROCESSING ) {
                    await new Promise( resolve => setTimeout( resolve, 2000 ) );
                }

            } while ( result.status === extractStatuses.PROCESSING );

            if ( result.status === extractStatuses.FAILED ) {
                throw new Error( result.error || 'Extract failed' );
            }

            const fetchResponse = await fetch( result.downloadUrl );

            if ( !fetchResponse.ok ) {
                throw new Error( `Failed to fetch file: ${ fetchResponse.status }` );
            }
            
            let csv = await fetchResponse.text();

            if ( typeof onDownload === 'function' ) {
                const extract = parseCsv( csv );
                const modifiedExtract = await onDownload( file, extract );
                csv = createCsv( modifiedExtract || extract );
            }

            if ( typeof getColumnOrder === 'function' ) {
                const extract = parseCsv( csv );
                csv = createCsv( extract, additionalColumns, getColumnOrder );
            }

            const data = new Blob( [ csv ], { type: 'text/csv;charset=utf-8;' } );
            setExporting( false );

            download( data, `${ file.name }-export.csv` );

            if ( typeof onFileExport === 'function' ) {
                onFileExport( file, data );
            }
        } catch ( error ) {
            console.error( 'Error extracting file', error.status || error.message );
            setExporting( false );
        }
    };

    const handleExportMenuClick = ( { key } ) => {
        exportFile( key );
        setMenuVisible( false );
    };

    const canExport = file && ( file.failedAt || file.sharedStagingCompletedAt );

    if ( !file || !canExport || file.totalErrorCount === 0 ) {
        return (
            <Button
                type="primary"
                ghost={ true }
                onClick={ () => exportFile( extractTypes.ALL ) }
                disabled={ disabled || !fileId || !file || !canExport || exporting }
                loading={ exporting }
                icon={ <DownloadOutlined /> }
            >
                <span>Export File</span>
            </Button>
        );
    }

    const fileLoaded = file.status === 'LOADED';
    const menuItems = [
        {
            key: fileLoaded ? extractTypes.LOADED : extractTypes.LOAD_SKIPPED,
            label: fileLoaded ? 'Loaded Records' : 'Validated Records'
        },
        {
            key: extractTypes.ERROR,
            label: 'Error Records'
        },
        {
            key: extractTypes.ALL,
            label: 'All Records'
        }
    ];

    return (
        <Dropdown
            trigger={ [ 'click' ] }
            open={ menuVisible }
            onOpenChange={ setMenuVisible }
            menu={ { items: menuItems, onClick: handleExportMenuClick } }
        >
            <Button
                type="primary"
                ghost={ true }
                disabled={ disabled || exporting }
                loading={ exporting }
                icon={ <DownloadOutlined /> }
            >
                <span>Export File</span>
                <DownOutlined className="menu-arrow" />
            </Button>
        </Dropdown>
    );
};

export default FileExtractButton;